import { useId, useState, type KeyboardEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { cx } from '../lib/cx'
import { Icon } from './Icon'
import './PinPad.css'

export interface PinPadProps {
  /** What the PIN is for, e.g. "Enter your PIN to approve this loan". */
  label: string
  /** The PIN's exact length (4–6). When set, the pad submits as soon as it is reached. */
  length?: number
  /** Without `length`: the shortest PIN accepted, then a Done key submits. Default 4. */
  minLength?: number
  /** Without `length`: the longest PIN accepted. Default 6. */
  maxLength?: number
  onComplete: (pin: string) => void
  /** A wrong PIN, for example. A new error clears the dots so she can try again. */
  error?: string
  hint?: string
  disabled?: boolean
  className?: string
}

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9'] as const

/**
 * Large-key PIN entry for step-up (approvals, payouts, destination changes). Digits
 * show as dots; screen readers hear only how many have been entered. Works with the
 * on-screen keys or a keyboard (digits, Backspace, Enter).
 */
export function PinPad({
  label,
  length,
  minLength = 4,
  maxLength = 6,
  onComplete,
  error,
  hint,
  disabled = false,
  className,
}: PinPadProps) {
  const { t } = useTranslation()
  const labelId = useId()
  const hintId = useId()
  const errorId = useId()
  const [pin, setPin] = useState('')
  const max = length ?? maxLength
  const min = length ?? minLength
  const canSubmit = !disabled && pin.length >= min && pin.length <= max

  // A new error means the attempt failed: clear the dots.
  const [shownError, setShownError] = useState(error)
  if (error !== shownError) {
    setShownError(error)
    if (error) setPin('')
  }

  const press = (digit: string) => {
    if (disabled || pin.length >= max) return
    const next = pin + digit
    setPin(next)
    if (length !== undefined && next.length === length) onComplete(next)
  }

  const backspace = () => {
    if (!disabled) setPin((current) => current.slice(0, -1))
  }

  const submit = () => {
    if (canSubmit) onComplete(pin)
  }

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.ctrlKey || event.metaKey || event.altKey) return
    if (/^[0-9]$/.test(event.key)) {
      event.preventDefault()
      press(event.key)
    } else if (event.key === 'Backspace') {
      event.preventDefault()
      backspace()
    } else if (event.key === 'Enter' && length === undefined && event.target === event.currentTarget) {
      // Enter on a focused key presses that key; Enter on the pad itself submits.
      event.preventDefault()
      submit()
    }
  }

  const progress =
    length !== undefined
      ? t('kit.pinPad.progress', { count: pin.length, total: length })
      : t('kit.pinPad.progressRange', { count: pin.length, min: minLength, max: maxLength })

  return (
    <div
      className={cx('pin-pad', disabled && 'pin-pad--disabled', className)}
      role="group"
      aria-labelledby={labelId}
      aria-describedby={[hint ? hintId : '', error ? errorId : ''].filter(Boolean).join(' ') || undefined}
      // Focusable so a keyboard can type the PIN straight in (the officer console).
      tabIndex={disabled ? -1 : 0}
      onKeyDown={onKeyDown}
    >
      <p id={labelId} className="pin-pad__label t-heading">
        {label}
      </p>
      {hint && (
        <p id={hintId} className="pin-pad__hint t-muted">
          {hint}
        </p>
      )}
      <div className={cx('pin-pad__dots', error && 'pin-pad__dots--error')} aria-hidden="true">
        {Array.from({ length: max }, (_, i) => (
          <span key={i} className={cx('pin-pad__dot', i < pin.length && 'pin-pad__dot--filled', i >= min && 'pin-pad__dot--optional')} />
        ))}
      </div>
      <p className="visually-hidden" aria-live="polite">
        {progress}
      </p>
      {error && (
        <p id={errorId} className="pin-pad__error" role="alert">
          <Icon name="error" size={20} />
          {error}
        </p>
      )}
      <div className="pin-pad__keys">
        {KEYS.map((key) => (
          <button key={key} type="button" className="pin-pad__key" onClick={() => press(key)} disabled={disabled}>
            {key}
          </button>
        ))}
        {length === undefined ? (
          <button
            type="button"
            className="pin-pad__key pin-pad__key--action"
            onClick={submit}
            disabled={!canSubmit}
          >
            <Icon name="check" label={t('kit.pinPad.done')} />
          </button>
        ) : (
          <span aria-hidden="true" />
        )}
        <button type="button" className="pin-pad__key" onClick={() => press('0')} disabled={disabled}>
          0
        </button>
        <button
          type="button"
          className="pin-pad__key pin-pad__key--action"
          onClick={backspace}
          disabled={disabled || pin.length === 0}
        >
          <Icon name="backspace" label={t('kit.pinPad.delete')} />
        </button>
      </div>
    </div>
  )
}
